import { useCallback, useEffect, useState } from 'react'
import { EnumCommodity } from '../../../../../api'
import { PriceComponentInstancesDataTableModel } from '../../../../../api/Argentia/endpoints/priceComponents'
import { PricePeriodTableModel } from '../../../../../api/Argentia/endpoints/pricePeriods/model/pricePeriods'
import { EnumPriceComponentReference } from '../../../../../api/Argentia/enums'
import {
  mapPriceComponentModelV2ToPriceComponentInstanceDataTable,
  mapPricePeriodTable
} from '../../../pricing.mapper'
import {
  fetchPriceComponentByPcTypeID,
  fetchPriceComponentDefaultValue,
  fetchPriceComponentInstancesBySearch,
  fetchPricePeriodByID
} from '../../../pricing.service'

export const useGetPriceComponentByPcTypeID = (
  setError: (errorMsg: string) => void,
  priceComponentID: number,
  commodity: EnumCommodity,
  companyId: number,
  fetchTrigger: boolean = true
) => {
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [fetchData, setFetchData] = useState<PriceComponentInstancesDataTableModel[]>([])

  const getPriceComponentByPcTypeID = useCallback(() => {
    setIsFetching(true)
    fetchPriceComponentByPcTypeID(priceComponentID, commodity, companyId)
      .then(response => {
        if (response.success && response.data) {
          setFetchData(mapPriceComponentModelV2ToPriceComponentInstanceDataTable(response.data))
        } else {
          setError(
            'priceComponentTypeInstances:notifications.fetchErrors.getPriceComponentByPcTypeID'
          )
        }
      })
      .finally(() => {
        setIsFetching(false)
      })
  }, [])

  useEffect(() => {
    fetchTrigger && getPriceComponentByPcTypeID()
  }, [fetchTrigger])

  return { isFetching, fetchData, getPriceComponentByPcTypeID }
}

export const useGetPricePeriodByID = (
  setError: (errorMsg: string) => void,
  priceID: number,
  fetchTrigger: boolean = true
) => {
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [fetchData, setFetchData] = useState<PricePeriodTableModel>()

  const getPricePeriodByID = useCallback((priceID: number) => {
    setIsFetching(true)
    fetchPricePeriodByID(priceID)
      .then(response => {
        if (response.success && response.data) {
          setFetchData(mapPricePeriodTable(response.data))
        } else {
          setError('priceComponentTypeInstances:notifications.fetchErrors.getPricePeriodByID')
        }
      })
      .finally(() => {
        setIsFetching(false)
      })
  }, [])

  useEffect(() => {
    fetchTrigger && getPricePeriodByID(priceID)
  }, [fetchTrigger])

  return { isFetching, fetchData, getPricePeriodByID }
}

export const useGetPriceComponentDefaultValue = (
  setError: (errorMsg: string) => void,
  priceComponentID: number,
  referenceType: EnumPriceComponentReference,
  commodity: EnumCommodity,
  companyId: number,
  fetchTrigger: boolean = true
) => {
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [fetchData, setFetchData] = useState<PriceComponentInstancesDataTableModel | undefined>(
    undefined
  )

  const getPriceComponentDefaultValue = useCallback(() => {
    setIsFetching(true)
    fetchPriceComponentDefaultValue(priceComponentID, referenceType, commodity, companyId)
      .then(response => {
        if (response.success && response.data) {
          const mapped = mapPriceComponentModelV2ToPriceComponentInstanceDataTable([response.data])
          setFetchData(mapped[0])
        } else {
          setError(
            'priceComponentTypeInstances:notifications.fetchErrors.getPriceComponentDefaultValue'
          )
        }
      })
      .finally(() => {
        setIsFetching(false)
      })
  }, [priceComponentID, commodity, companyId])

  useEffect(() => {
    fetchTrigger && getPriceComponentDefaultValue()
  }, [fetchTrigger, getPriceComponentDefaultValue])

  return { isFetching, fetchData, getPriceComponentDefaultValue }
}

export const useSearchPriceComponentInstances = (setError: (errorMsg: string) => void) => {
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [fetchData, setFetchData] = useState<PriceComponentInstancesDataTableModel[]>([])

  const searchPriceComponentInstances = useCallback(
    (
      priceComponentTypeId: number,
      commodity: number,
      companyId: number,
      referenceType: number,
      searchText?: string,
      leadIds?: number[],
      tollIds?: number[],
      presionTensionIds?: number[],
      regionImpuestosIds?: number[]
    ) => {
      setIsFetching(true)
      fetchPriceComponentInstancesBySearch({
        priceComponentTypeId,
        commodity,
        companyId,
        referenceType,
        searchText,
        leadIds,
        tollIds,
        presionTensionIds,
        regionImpuestosIds
      })
        .then(response => {
          if (response.success && response.data) {
            setFetchData(mapPriceComponentModelV2ToPriceComponentInstanceDataTable(response.data))
          } else {
            setError(
              'priceComponentTypeInstances:notifications.fetchErrors.searchPriceComponentInstances'
            )
          }
        })
        .finally(() => {
          setIsFetching(false)
        })
    },
    [setError]
  )

  return { isFetching, fetchData, searchPriceComponentInstances }
}
